import { createSlice } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid';

// ----------------------------------------------------------------------

const initialState = {
    blocks: [],
    selectedId: null,
    editingId: null,
    inline: {
        visible: false,
        rect: null, // { top, left, width, height, containerTop, containerLeft }
        blockId: null,
    },
    previewDevice: 'ipad',
    isInspectorOpen: false,
    mode: 'edit', // edit | preview
    openAddDrawer: false,
};

const slice = createSlice({
    name: 'pageCreator',
    initialState,
    reducers: {
        // Basic Setters
        setBlocks(state, action) {
            const blocks = action.payload;
            // Ensure at least one empty text block if empty
            console.log('pageCreator:setBlocks called, blocksLength=', Array.isArray(blocks) ? blocks.length : 'non-array');
            if (!blocks || blocks.length === 0) {
                state.blocks = [{
                    id: nanoid(),
                    type: 'text',
                    data: { text: '' }
                }];
            } else {
                state.blocks = blocks;
            }
        },
        setPreviewDevice(state, action) {
            state.previewDevice = action.payload;
        },
        setOpenAddDrawer(state, action) {
            state.openAddDrawer = action.payload;
        },
        setMode(state, action) {
            state.mode = action.payload;
        },

        // Block Management
        addBlock(state, action) {
            const block = action.payload;
            const newBlock = {
                id: nanoid(),
                type: block.type || 'text',
                data: block.data || {},
            };
            state.blocks.push(newBlock);
        },
        updateBlock(state, action) {
            const { id, data } = action.payload;
            console.log('pageCreator:updateBlock', { id, dataSummary: data && Object.keys(data) });
            const block = state.blocks.find((b) => b.id === id);
            if (block) {
                block.data = { ...block.data, ...data };
            }
        },
        removeBlock(state, action) {
            const id = action.payload;
            state.blocks = state.blocks.filter((b) => b.id !== id);
        },
        insertBlockAfter(state, action) {
            const { afterId, newBlock } = action.payload;
            const index = state.blocks.findIndex((b) => b.id === afterId);
            if (index === -1) {
                state.blocks.push(newBlock);
            } else {
                state.blocks.splice(index + 1, 0, newBlock);
            }
        },
        // Useful for complex deep updates if needed, though usually updateBlock is enough
        patchBlock(state, action) {
            const { id, patch } = action.payload;
            console.log('pageCreator:patchBlock', { id, patchType: typeof patch });
            const block = state.blocks.find((b) => b.id === id);
            if (block) {
                // patch can be object or logic handled in component before dispatch
                // reducing complexity here by assuming patch is already the object to merge
                block.data = { ...block.data, ...patch };
            }
        },

        // Selection & Editing
        setSelectedId(state, action) {
            state.selectedId = action.payload;
        },
        setEditingId(state, action) {
            state.editingId = action.payload;
        },
        clearEditing(state) {
            state.editingId = null;
        },
        clearSelection(state) {
            state.selectedId = null;
        },

        // Inline Toolbar
        setInline(state, action) {
            const pos = action.payload;
            state.inline = { visible: true, ...pos };
        },
        hideInline(state) {
            state.inline = { visible: false, rect: null, blockId: null };
        },

        // Inspector
        openInspector(state, action) {
            state.selectedId = action.payload;
            state.editingId = null; // Exit editing mode when opening inspector
            state.isInspectorOpen = true;
        },
        closeInspector(state) {
            state.selectedId = null;
            state.isInspectorOpen = false;
        },

        // Mode Toggle
        toggleMode(state) {
            state.mode = state.mode === 'edit' ? 'preview' : 'edit';
            state.selectedId = null;
            state.editingId = null;
            state.inline = { visible: false, rect: null, blockId: null };
        }
    },
});

// Reducer
export default slice.reducer;

// Actions
export const {
    setBlocks,
    setPreviewDevice,
    setOpenAddDrawer,
    setMode,
    addBlock,
    updateBlock,
    removeBlock,
    insertBlockAfter,
    patchBlock,
    setSelectedId,
    setEditingId,
    clearEditing,
    clearSelection,
    setInline,
    hideInline,
    openInspector,
    closeInspector,
    toggleMode
} = slice.actions;

// ----------------------------------------------------------------------
// Helpers (Not Redux actions, but utility functions for data conversion)

/**
 * Normalizes EditorJS data for the Redux store
 */
export function normalizeEditorJS(editorJsData) {
    let input = [];
    if (Array.isArray(editorJsData)) {
        input = editorJsData;
    } else if (editorJsData && Array.isArray(editorJsData.blocks)) {
        input = editorJsData.blocks;
    }

    // If empty, return empty array (setBlocks will handle default block)
    if (input.length === 0) return [];

    return input.map((b) => {
        let type = b.type || 'text';
        let data = b.data || {};

        // === Schema Migration (External -> Internal) ===

        // 1. Paragraph -> Text
        if (type === 'paragraph') {
            type = 'text';
        }

        // 2. Text Block Pro -> Text Pro
        if (type === 'text_block_pro') {
            type = 'text_pro';
        }

        // 3. Icon List -> Icon List Pro
        // Reference uses 'icon_list' with { settings: {...} }
        if (type === 'icon_list') {
            type = 'icon_list_pro';
            // Unwrap settings if present (preferred internal state is flat or as-is)
            // But checking IconListProBlock, it works with 'data' directly.
            // If data comes as { settings: { ... } }, we should probably spread it?
            // Actually, IconListPro inspector works with 'data'.
            // Let's assume internal data = external data.settings
            if (data.settings) {
                data = { ...data.settings };
            }
        }

        return {
            id: b.id || nanoid(8),
            type,
            data,
            tunes: b.tunes || {},
        };
    });
}

/**
 * Converts Redux store blocks back to EditorJS format
 */
export function toEditorJS(blocks) {
    return {
        time: Date.now(),
        version: '2.30.6',
        blocks: (blocks || []).map((b) => {
            let type = b.type;
            let data = { ...b.data };

            // === Schema Migration (Internal -> External) ===

            // 1. Text -> Paragraph
            if (type === 'text') {
                type = 'paragraph';
            }

            // 2. Text Pro -> Text Block Pro
            if (type === 'text_pro') {
                type = 'text_block_pro';
            }

            // 3. Icon List Pro -> Icon List
            if (type === 'icon_list_pro') {
                type = 'icon_list';
                if (!data.settings) {
                    data = { settings: { ...data } };
                }
            }

            return {
                id: b.id,
                type,
                data,
                tunes: b.tunes || {},
            };
        }),
    };
}
